import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="touring-booking-form"
export default class extends Controller {
  static targets = ["gigCheckbox", "contactContainer"]
  static values = { 
    touringId: String,
    nearbyContactsUrl: String
  }

  connect() {
    // Set up the nearby contacts URL based on touring ID if not provided
    if (!this.nearbyContactsUrlValue && this.touringIdValue) {
      this.nearbyContactsUrlValue = `/admin/tourings/${this.touringIdValue}/bookings/nearby_contacts`
    }
    
    // Initialize contact containers
    this.initializeContactContainers()
  }

  disconnect() {
    // Clean up any pending requests
    if (this.currentRequest) {
      this.currentRequest.abort()
      this.currentRequest = null
    }
  }

  gigChanged(event) {
    const checkbox = event.target
    const gigId = checkbox.value
    const isChecked = checkbox.checked
    
    if (isChecked) {
      this.fetchNearbyContacts(gigId)
    } else {
      this.clearContacts(gigId)
    }
    
    // Update form state for accessibility and UX
    this.updateFormState()
  }

  async fetchNearbyContacts(gigId) {
    if (!this.nearbyContactsUrlValue || !gigId) {
      console.error("Missing URL or gig ID for nearby contacts fetch")
      return
    }

    try {
      // Show loading state
      this.showLoadingState(gigId)
      
      // Get all currently selected gig IDs
      const selectedGigIds = this.getSelectedGigIds()
      
      // Create form data for the request
      const formData = new FormData()
      formData.append("gig_id", gigId)
      
      // Add all selected gig IDs
      selectedGigIds.forEach(id => {
        formData.append("selected_gig_ids[]", id)
      })

      // Cancel any existing request
      if (this.currentRequest) {
        this.currentRequest.abort()
      }

      // Make the request with timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout
      
      this.currentRequest = fetch(this.nearbyContactsUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": this.getCSRFToken()
        },
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)

      const response = await this.currentRequest
      
      if (response.ok) {
        const turboStreamHTML = await response.text()
        
        // Let Turbo handle the stream response
        if (turboStreamHTML) {
          Turbo.renderStreamMessage(turboStreamHTML)
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching nearby contacts:", error)
        let errorMessage = "Une erreur est survenue lors du chargement des contacts"
        
        if (error.name === "TypeError" && !navigator.onLine) {
          errorMessage = "Pas de connexion internet. Vérifiez votre connexion."
        } else if (error.message.includes("timeout")) {
          errorMessage = "Délai d'attente dépassé. Veuillez réessayer."
        }
        
        this.showErrorState(gigId, errorMessage)
      }
    } finally {
      this.currentRequest = null
      this.hideLoadingState(gigId)
    }
  }

  clearContacts(gigId) {
    const contactContainer = this.findContactContainer(gigId)
    if (contactContainer) {
      contactContainer.innerHTML = ""
      contactContainer.classList.remove("loading", "error")
    }
  }

  getSelectedGigIds() {
    return this.gigCheckboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute("content") : ""
  }

  findContactContainer(gigId) {
    return document.getElementById(`gig_${gigId}_contacts`) || 
           this.contactContainerTargets.find(container => 
             container.dataset.gigId === gigId
           )
  }

  showLoadingState(gigId) {
    const container = this.findContactContainer(gigId)
    if (container) {
      container.classList.add("loading")
      container.classList.remove("error")
      container.innerHTML = `
        <div class="flex items-center gap-2 p-3 text-midgray">
          <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Chargement des contacts à proximité...</span>
        </div>
      `
    }
  }

  hideLoadingState(gigId) {
    const container = this.findContactContainer(gigId)
    if (container) {
      container.classList.remove("loading")
    }
  }

  showErrorState(gigId, message) {
    const container = this.findContactContainer(gigId)
    if (container) {
      container.classList.add("error")
      container.classList.remove("loading")
      container.innerHTML = `
        <div class="flex items-center gap-2 p-3 text-red-600 bg-red-50 border border-red-200 rounded">
          <svg class="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <span>${message}</span>
        </div>
      `
    }
  }

  initializeContactContainers() {
    // Ensure all contact containers are properly initialized
    this.gigCheckboxTargets.forEach(checkbox => {
      const gigId = checkbox.value
      const container = this.findContactContainer(gigId)
      if (container) {
        container.dataset.gigId = gigId
        if (!this.contactContainerTargets.includes(container)) {
          container.dataset.touringBookingFormTarget = "contactContainer"
        }
      }
    })
  }

  updateFormState() {
    // Update form state for better UX
    const selectedCount = this.getSelectedGigIds().length
    
    // Dispatch custom event for other controllers to listen to
    this.dispatch("gigSelectionChanged", {
      detail: {
        selectedCount,
        selectedGigIds: this.getSelectedGigIds()
      }
    })
  }

  // Getter for easy access to selected gigs count
  get selectedGigsCount() {
    return this.getSelectedGigIds().length
  }

  // Public method to manually refresh contacts for a specific gig
  refreshContacts(gigId) {
    const checkbox = this.gigCheckboxTargets.find(cb => cb.value === gigId)
    if (checkbox && checkbox.checked) {
      this.fetchNearbyContacts(gigId)
    }
  }

  // Handle manual contact selection from Tom Select
  manualContactSelected(event) {
    const select = event.target
    const gigId = select.dataset.gigId
    const selectedContactIds = Array.from(select.selectedOptions).map(option => option.value)

    if (!gigId || selectedContactIds.length === 0) return

    // Create hidden checkboxes for selected contacts
    const container = this.findContactContainer(gigId)
    if (!container) return

    // Find or create a hidden inputs container
    let hiddenContainer = container.querySelector('.manual-contact-checkboxes')
    if (!hiddenContainer) {
      hiddenContainer = document.createElement('div')
      hiddenContainer.className = 'manual-contact-checkboxes hidden'
      container.appendChild(hiddenContainer)
    }

    // Clear existing hidden checkboxes
    hiddenContainer.innerHTML = ''

    // Create a hidden checkbox for each selected contact
    selectedContactIds.forEach(contactId => {
      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.name = `booking[gig_${gigId}][contact_ids][]`
      checkbox.value = contactId
      checkbox.checked = true
      checkbox.style.display = 'none'
      hiddenContainer.appendChild(checkbox)
    })
  }
}